import { TEMPLATE_FORMAT } from '@/lib/noteStyle';
import { bulletListClassName } from '@/pages/onboarding/onboarding.styles';
import type { SampleNoteProps } from '@/pages/onboarding/onboarding.types';
import type { NoteFormatChoice } from '@/types/noteStyle';
import { Box, Stack, Text } from '@careflux/primitives';

/** Sentence boundaries: a full stop followed by whitespace. */
const SENTENCE_BREAK = /(?<=\.)\s+/;

/** "Eftir sniðmáti" mixes layouts, as a template would: the last section as bullets, the rest as prose. */
const sectionIsBullets = (format: NoteFormatChoice, index: number, count: number) =>
  format === TEMPLATE_FORMAT ? index === count - 1 : format === 'bullets';

/** Example note for the chosen detail level: prose, one bullet per sentence, or mixed ("Eftir sniðmáti"). */
export const SampleNote = ({ copy, detail, format }: SampleNoteProps) => (
  <Box as="article" aria-label={copy.sampleHeader} border="default" rounded="xl" paddingX="lg" paddingY="md">
    <Box as="header" display="flex" align="baseline" justify="between" gap="sm">
      <Text size="xs" weight="semibold" color="muted" transform="uppercase">
        {copy.sampleHeader}
      </Text>
      <Text size="xs" weight="medium" color="muted">
        {copy.sampleDescriptions[detail]}
      </Text>
    </Box>
    <Stack gap="sm" paddingY="sm">
      {copy.samples[detail].map((section, index, sections) => (
        <Stack as="section" key={section.heading} gap="xs">
          <Text asChild size="sm" weight="semibold">
            <h3>{section.heading}</h3>
          </Text>
          {sectionIsBullets(format, index, sections.length) ? (
            <Text asChild size="sm">
              <ul className={bulletListClassName}>
                {section.body.split(SENTENCE_BREAK).map((sentence, sentenceIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static sample text; sentences may repeat
                  <li key={sentenceIndex}>{sentence}</li>
                ))}
              </ul>
            </Text>
          ) : (
            <Text asChild size="sm">
              <p>{section.body}</p>
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  </Box>
);
